/**
 * Schematic Editor Page
 * Main component for creating and editing TS100 schematics
 */

import { useState, useCallback } from 'react';
import { ConfigForm, type SchematicConfig } from './ConfigForm';
import { SvgPreview } from '../../components/SvgPreview';
import { projectsApi, drawingsApi } from '../../services/api';

const DEFAULT_CONFIG: SchematicConfig = {
  drawingNumber: '00001T',
  projectName: 'New Project',
  // Water
  zeroTdsCapacity: 2000,
  hasCentralUnit: false,
  // Pressure tank
  pressureTankCapacity: 20,
  pressureTankCount: 1,
  // Compressed Air
  compressorCapacity: 2500,
  // Control
  controllers: [
    {
      model: 'CPLC3',
      systemType: 'in-duct',
      zones: [
        { unitCount: 2, modelVariant: 2, unitModel: 'TS100-2' },
        { unitCount: 2, modelVariant: 2, unitModel: 'TS100-2' },
        { unitCount: 1, modelVariant: 2, unitModel: 'TS100-2' }
      ]
    }
  ],
  controllerModel: 'CPLC3',
  zoneCount: 3,
  hasBacnet: true,
  areas: [
    {
      areaLabel: 'Area 1',
      ductWidth: 28,
      ductHeight: 6,
      ductDepth: 14,
      unitModel: 'TS100-2',
      nozzleSpacing: [5, 3, 4, 3, 4, 3, 5]
    }
  ],
  titleBlock: {
    title: 'SMART FOG TS100 SYSTEM',
    scale: '1 : 20',
    drawn: '',
    checked: '',
    engAppr: '',
    version: '1.0'
  }
};

export function SchematicEditor() {
  const [config, setConfig] = useState<SchematicConfig>(DEFAULT_CONFIG);
  const [svgPages, setSvgPages] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [drawingId, setDrawingId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    setError(null);

    try {
      // Create or get project
      let currentProjectId = projectId;
      if (!currentProjectId) {
        const projectRes = await projectsApi.create({
          name: config.projectName
        });
        if (!projectRes.success || !projectRes.data) {
          throw new Error(projectRes.error || 'Failed to create project');
        }
        currentProjectId = projectRes.data.id;
        setProjectId(currentProjectId);
      }

      // Create drawing with config
      const drawingRes = await projectsApi.createDrawing(currentProjectId, {
        drawingNumber: config.drawingNumber,
        type: 'ts100_schematic',
        status: 'draft',
        config: config as unknown as Record<string, unknown>
      });

      if (!drawingRes.success || !drawingRes.data) {
        throw new Error(drawingRes.error || 'Failed to create drawing');
      }

      const newDrawingId = drawingRes.data.id;
      setDrawingId(newDrawingId);

      // Get SVG
      const svgRes = await drawingsApi.getSvg(newDrawingId);
      if (!svgRes.success || !svgRes.data) {
        throw new Error(svgRes.error || 'Failed to generate SVG');
      }

      setSvgPages(svgRes.data.pages);
      setCurrentPage(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsGenerating(false);
    }
  }, [config, projectId]);

  const handleDownloadPdf = useCallback(async () => {
    if (!drawingId) {
      // Generate first if no drawing exists
      await handleGenerate();
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Generate PDF
      const pdfRes = await drawingsApi.generatePdf(drawingId);
      if (!pdfRes.success) {
        throw new Error(pdfRes.error || 'Failed to generate PDF');
      }

      // Download PDF
      window.open(drawingsApi.getPdfUrl(drawingId), '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsGenerating(false);
    }
  }, [drawingId, handleGenerate]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return (
    <div className="main editor-layout">
      <ConfigForm
        config={config}
        onChange={setConfig}
        onGenerate={handleGenerate}
        onDownloadPdf={handleDownloadPdf}
        isGenerating={isGenerating}
      />

      <div className="editor-content">
        {error && <div className="error">{error}</div>}

        <SvgPreview
          svgContent={svgPages[currentPage]}
          pageCount={svgPages.length}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          isLoading={isGenerating}
        />
      </div>
    </div>
  );
}
